import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import * as actions from '../redux/actions';
import { getLSKey, LS_KEYS } from '../../../utils/localStorageHelper';

const LabsStockContext = createContext();

export const LabsStockProvider = ({ children, initialData = {} }) => {
  const dispatch = useDispatch();
  const { lab_id } = useParams();
  const [showVarianceReport, setShowVarianceReport] = useState(false);
  // const { user } = useSelector((state) => state.authReducer);
  // const { labs } = useSelector((state) => state.authReducer);
  // const { newStockManagementRegister, varianceReports } = useSelector((state) => state.labsStockManagementReducer);
  const user = useSelector((state) => state.authReducer.user);
  const labs = useSelector((state) => state.authReducer.labs);
  const newStockManagementRegister = useSelector((state) => state.labsStockManagementReducer.newStockManagementRegister);
  const reduxStockTakes = useSelector((state) => state.labsStockManagementReducer.stockTakes);
  const reduxStockTakeErrors = useSelector((state) => state.labsStockManagementReducer.stockTakeErrors);
  const varianceReports = useSelector((state) => state.labsStockManagementReducer.varianceReports);
  const allstockManagementRegisters = useSelector((state) => state.labsStockManagementReducer.allstockManagementRegisters);

  const [reportsGenerated, setReportsGenerated] = useState({});

  const [isAmending, setIsAmending] = useState(false);
  const [finalizedStockTakes, setFinalizedStockTakes] = useState({});
  const [stockTakeDataMap, setStockTakeDataMap] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [isCreatingStockTake, setIsCreatingStockTake] = useState(false);
  const [deletableRegisters, setDeletableRegisters] = useState({});

  const [images, setImages] = useState([]);
  const [attachPopupVisible, setAttachPopupVisible] = useState(false);
  const [fields, setFields] = useState([]);
  const [currentUpdateObj, setCurrentUpdateObj] = useState({});
  const [registerSequences, setRegisterSequences] = useState({});

  const selectedLab = labs?.find((lab) => (lab?.labSetup?.id || lab?.id) == lab_id);
  const lsselectedLabsKey = getLSKey(LS_KEYS.SELECTED_LABS, user?.id);
  const lsLab = JSON.parse(localStorage.getItem(lsselectedLabsKey) || "[]")?.find((lab) => (lab?.labSetup?.id || lab?.id) == lab_id);
  const labSetupId = selectedLab?.labSetup?.id || lsLab?.labSetup?.id || lab_id;
  const wareHouseId = selectedLab?.labSetup?.wareHouseId || lsLab?.labSetup?.wareHouseId;
  const labName = selectedLab?.labSetup?.labName || selectedLab?.labName || lsLab?.labSetup?.labName || lsLab?.labName;

  const getWarehouseName = (name) => {
    if (!name) return 'UNKNOWN';
    return name.split(' - ')[0];
  };

  const generateUniqueId = (currentLabSetupId, warehouseName) => {
    const storageKey = `stock_register_seq_${currentLabSetupId}`;
    
    let currentSeq = registerSequences[storageKey] || 
                     parseInt(localStorage.getItem(storageKey) || '0');
    
    const newSeq = currentSeq + 1;
    
    localStorage.setItem(storageKey, newSeq);
    setRegisterSequences(prev => ({ ...prev, [storageKey]: newSeq }));
    
    return `${warehouseName}-${String(newSeq).padStart(3, '0')}`;
  };

  useEffect(() => {
    setExpandedRowKeys([]);
    setDeletableRegisters({});
  }, [labSetupId]);

useEffect(() => {
  if (!allstockManagementRegisters?.length) return;

  const alreadyGenerated = {};

  allstockManagementRegisters.forEach(register => {
    const hasVariance =
      register.varianceReports?.length > 0 ||
      register.variance_reports?.length > 0;

    const isCompleted =
      register.status?.toLowerCase() === "completed";

    if (hasVariance || isCompleted) {
      alreadyGenerated[String(register.id)] = true;
    }
  });

  setReportsGenerated(alreadyGenerated);
}, [allstockManagementRegisters]);

  useEffect(() => {
    if (allstockManagementRegisters && allstockManagementRegisters.length > 0) {
      const finalized = {};
      allstockManagementRegisters.forEach(register => {
        if (register.finalizedAt) {
          finalized[String(register.id)] = register.finalizedAt;
        }
      });
      if (Object.keys(finalized).length > 0) {
        setFinalizedStockTakes(prev => ({ ...prev, ...finalized }));
      }
    }
  }, [allstockManagementRegisters]);

  useEffect(() => {
    if (reduxStockTakes && Object.keys(reduxStockTakes).length > 0) {
      const updatedMap = {};
      Object.entries(reduxStockTakes).forEach(([registerId, stockTakes]) => {
        updatedMap[registerId] = stockTakes.map(stockTake => ({
          id: stockTake.id,
          key: stockTake.id,
          scannedCode: stockTake.scannedCode || "",
          lotNo: stockTake.lotNo || "",
          expiryDate: stockTake.expiryDate ? moment(stockTake.expiryDate).format("YYYY/MM/DD") : "",
          daysPastExpiry: stockTake.daysPastExpirey || stockTake.daysPastExpiry || 0,
          barCode: stockTake.barcode || stockTake.barCode || "",
          stockItemLookup: stockTake.stockItemLookup || "",
          level: stockTake.level || "",
        }));
      });
      setStockTakeDataMap(updatedMap);
    }
  }, [reduxStockTakes]);

  useEffect(() => {
    if (allstockManagementRegisters && allstockManagementRegisters.length > 0 && labSetupId) {
      const warehouseName = getWarehouseName(labName);
      const storageKey = `stock_register_seq_${labSetupId}`;
      
      const existingSeqs = allstockManagementRegisters
        .map(reg => {
          const match = reg.uniqueId?.match(/^([A-Za-z0-9]+)-(\d+)$/);
          if (match && match[1] === warehouseName) {
            return parseInt(match[2], 10);
          }
          return 0;
        })
        .filter(seq => seq > 0);
      
      const maxSeq = existingSeqs.length > 0 ? Math.max(...existingSeqs) : 0;
      const storedSeq = parseInt(localStorage.getItem(storageKey) || '0', 10);
      const currentMaxSeq = Math.max(maxSeq, storedSeq);
      
      if (currentMaxSeq > 0) {
        localStorage.setItem(storageKey, currentMaxSeq);
        setRegisterSequences(prev => ({ ...prev, [storageKey]: currentMaxSeq }));
      }
    }
  }, [allstockManagementRegisters, labSetupId, labName]);

  const updateStockTakeData = (recordId, newData) => {
    setStockTakeDataMap(prev => ({
      ...prev,
      [recordId]: newData
    }));
  };

  const handleCompleteStockTake = (recordId) => {
    if (recordId) {
      setReportsGenerated(prev => ({ ...prev, [String(recordId)]: true }));
    }
    setIsAmending(false);
  };

  const finalizeStockTake = (registerId) => {
    dispatch(actions.updateStockManagementRegister(registerId, { finalizedAt: 'true' }));
    setFinalizedStockTakes(prev => ({ ...prev, [String(registerId)]: 'true' }));
  };

  const createStockTake = (registerId, stockTakeData) => {
    setIsCreatingStockTake(true);
    dispatch(actions.createStockTake(registerId, stockTakeData));
    setTimeout(() => setIsCreatingStockTake(false), 2000);
  };

  const clearStockTakeError = (registerId) => {
    dispatch(actions.clearStockTakeError(registerId));
  };

  const generateVarianceReport = (registerId) => {
    dispatch(actions.generateVarianceReport(registerId));
  };

  const createStockManagementRegister = (registerData) => {
    dispatch(actions.createStockManagementRegister(registerData));
  };

  const deleteStockManagementRegister = (registerId) => {
    dispatch(actions.deleteStockManagementRegister(registerId));

    setDeletableRegisters(prev => {
      const newState = { ...prev };
      delete newState[registerId];
      return newState;
    });
  };

  const finalizeRegister = (registerId) => {
    setDeletableRegisters(prev => ({
      ...prev,
      [registerId]: { canDelete: false, isExpanded: true }
    }));
  };

  const updateRegisterExpandedState = (registerId, isExpanded) => {
    setDeletableRegisters(prev => {
      const current = prev[registerId];
      if (!current) return prev;

      return {
        ...prev,
        [registerId]: { ...current, isExpanded }
      };
    });
  };

  const deleteStockTake = (stockTakeId, recordId) => {
    dispatch(actions.deleteStockTake(recordId, stockTakeId));

    const currentData = stockTakeDataMap[recordId] || [];
    const updatedData = currentData.filter(item => item.id !== stockTakeId && item.key !== stockTakeId);
    updateStockTakeData(recordId, updatedData);
  };

  const getColumnName = (i) => {
    const columnNames = [
      "Date",
      "Unique Id",
      "Sales Rep",
      "Omni Qty",
      "Stock Qty",
      "Variance Qty",
      "Status",
      "Attach",
      "Actions",
    ];
    return columnNames[i] || `Column ${i + 1}`;
  };

  const value = useMemo(() => {
    const mergedVarianceReports = { ...varianceReports };
    if (allstockManagementRegisters) {
      allstockManagementRegisters.forEach(register => {
  const reports = register.varianceReports || register.variance_reports || [];
    const regId = String(register.id);

    if (!mergedVarianceReports[regId]) {
      mergedVarianceReports[regId] = reports;
    }
      });
    }

    return {
      reportsGenerated,
      setReportsGenerated,
      isAmending,
      setIsAmending,
      finalizedStockTakes,
      finalizeStockTake,
      stockTakeDataMap,
      setStockTakeDataMap,
      updateStockTakeData,
      handleCompleteStockTake,
      createStockTake,
      createStockManagementRegister,
      deleteStockTake,
      deleteStockManagementRegister,
      finalizeRegister,
      updateRegisterExpandedState,
      deletableRegisters,
      setDeletableRegisters,
      isCreatingStockTake,
      stockTakeErrors: reduxStockTakeErrors,
      clearStockTakeError,
      generateVarianceReport,
      varianceReports: mergedVarianceReports,
      expandedRowKeys,
      setExpandedRowKeys,
      images,
      setImages,
      attachPopupVisible,
      setAttachPopupVisible,
      fields,
      setFields,
      currentUpdateObj,
      setCurrentUpdateObj,
      getColumnName,
      user,
      labSetupId,
      wareHouseId,
      generateUniqueId,
      labName,
      getWarehouseName,
      selectedLab,
      canExport: selectedLab?.permissions?.general?.export || false,
    };
  }, [
    reportsGenerated,
    isAmending,
    finalizedStockTakes,
    stockTakeDataMap,
    expandedRowKeys,
    isCreatingStockTake,
    deletableRegisters,
    reduxStockTakeErrors,
    varianceReports,
    allstockManagementRegisters,
    images,
    attachPopupVisible,
    fields,
    currentUpdateObj,
    user,
    labSetupId,
    wareHouseId,
    registerSequences,
    labName,
    selectedLab,
  ]);

  return (
    <LabsStockContext.Provider value={value}>
      {children}
    </LabsStockContext.Provider>
  );

};

export const useLabsStock = () => {
  const context = useContext(LabsStockContext);
  if (!context) {
    throw new Error('useLabsStock must be used within a LabsStockProvider');
  }
  return context;
};
